import React, { useCallback, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import api, { IOrdinalsMediaStreamItem } from 'api/api'
import AnswerButtons from 'components/answers/AnswerButtons'
import ArtifactMenu from 'components/answers/ArtifactMenu'
import { ordinalPlaylistMediaKey } from 'components/answers/artifactMenuPolicy'
import OrdinalChatModal from 'components/modals/OrdinalChatModal'
import type { Filter } from 'components/filters/useStreamFilters'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import answerModel from 'components/answers/answerModel'
import { cacheAnswer, getCachedAnswer, getCachedQuest } from 'state/cache'
import { draftAnswerStore } from 'state/draftAnswerStore'
import useStore from 'state/knovStore'
import {
    fetchAndCacheOrdinalAnswer,
    getOrdinalResolutionAction,
    withOrdinalShareOwner,
} from 'components/panels/ordinalActionResolution'
import { loadOrdinalProvenance } from 'components/answers/ordinalProvenance'
import { bsvOrdinalControlsEnabled } from 'components/panels/ordinalUpvalueFeature'
import styles from 'components/panels/ordinals-media-stream.module.scss'
import type { Answer, AnswerOrDraft } from 'types/answers'
import { ordinalChatEnabled } from 'lib/ordinalChat'
import { currentUserSnapshot } from 'lib/currentUserSnapshot'
import { surfaceClickIsClaimed } from 'lib/suppressNextClick'
import usePanelContext from 'refactor/hooks/usePanelContext'
import type { Quest } from 'types/quests'

type OrdinalActionDockProps = {
    item: IOrdinalsMediaStreamItem
    // 'caption': always-visible bar below the tile media (total left,
    // upvalue + comment right) plus the artifact menu over the media's top-right.
    // 'fullscreen': floating pill in the viewer.
    variant: 'caption' | 'fullscreen'
    openRight: (
        filter: Filter,
        opts?: { panelId?: string; meta?: unknown; ensureUnique?: boolean },
    ) => void
    // e.g. close the fullscreen viewer before jumping to the thread panel.
    onBeforeNavigate?: () => void
    onDetails?: (item: IOrdinalsMediaStreamItem) => void
}

/**
 * Answer controls for an ordinal ("artifact"): upvalue + reply.
 *
 * On stream tiles this renders as a caption bar below the media (option C:
 * zero occlusion of the artwork); in the fullscreen viewer it stays a
 * floating pill. Every ordinal maps to a dedicated system wrapper answer,
 * materialized on demand by POST bsv/ordinals/:id/ensure_thread. Once that
 * answer is in the client cache it renders the standard AnswerButtons bar.
 * Before hydration the same component renders from the stream's lightweight
 * signal summary and resolves the system-thread Answer before committing an action.
 *
 * Like a stream card in a thread channel, the caption bar's own surface opens
 * the system thread, and Reply drafts into it in the reply modal; posting
 * offers the panel's "View reply" pill rather than navigating.
 */
export default function OrdinalActionDock({
    item,
    variant,
    openRight,
    onBeforeNavigate,
    onDetails,
}: OrdinalActionDockProps) {
    const [ensured, setEnsured] = useState<{
        answerId: string
        childQuestId: string | null
    } | null>(null)
    const [chatThread, setChatThread] = useState<{
        answer: Answer
        questId: string
    } | null>(null)
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)
    const { offerAnswerJump } = usePanelContext()
    const prefetchedRef = useRef(false)
    const resolutionInFlightRef = useRef(false)

    // Stream parent IDs are wrapper-only. The minting Answer is serialized as
    // provenance separately and can never become an artifact control target.
    const knownAnswerId = ensured?.answerId || item.parent?.answer_id || null
    const knownChildQuestId = ensured?.childQuestId || item.parent?.child_quest_id || null
    const cachedAnswer = useCachedAnswer(knownAnswerId ? String(knownAnswerId) : null)

    const upvaluesEnabled = item.parent ? item.parent.upvalues_enabled !== false : true
    const summaryValueSats = Number(item.parent?.bsv_upvalue_amt || 0)
    // Value inherited from the minting post. Kept beside the wrapper's own total
    // rather than folded into it: AnswerButtons sums the two for display, and
    // the cached Answer carries the same field once it hydrates, so the pill
    // holds steady across the summary → cached-answer swap.
    const inheritedValueSats = Number(item.parent?.minting_bsv_upvalue_amt || 0)

    const previewAnswer = useMemo(
        () =>
            ({
                id: knownAnswerId ? String(knownAnswerId) : `ordinal-${item.id}`,
                user_id: item.owner_id || item.parent?.user?.id || '',
                quest_id: knownChildQuestId,
                signal_quest_id: knownChildQuestId,
                bsv_lock_amt: 0,
                bsv_upvalue_amt: summaryValueSats,
                minting_bsv_upvalue_amt: inheritedValueSats,
                bsv_locks: [],
                bsv_upvalues: [],
                reactions: [],
                upvalues_enabled: upvaluesEnabled,
                free_reactions_enabled: item.parent?.free_reactions_enabled === true,
                agent_model: '',
            } as Answer),
        [
            inheritedValueSats,
            item.id,
            item.owner_id,
            item.parent?.free_reactions_enabled,
            item.parent?.user?.id,
            knownAnswerId,
            knownChildQuestId,
            summaryValueSats,
            upvaluesEnabled,
        ],
    )

    const stopInteractiveEvent = useCallback((ev: React.SyntheticEvent) => {
        ev?.stopPropagation?.()
    }, [])

    // Resolve the system-thread answer, materializing the thread if needed.
    const resolveAnswer = useCallback(
        async ({ requireThread = false }: { requireThread?: boolean } = {}) => {
            const action = getOrdinalResolutionAction({
                knownAnswerId,
                knownChildQuestId,
                requireThread,
            })
            if (action === 'fetch_answer' && knownAnswerId) {
                try {
                    const answer = await fetchAndCacheOrdinalAnswer<Answer>(
                        knownAnswerId,
                        api.getAnswer,
                        cacheAnswer,
                    )
                    if (answer?.id) {
                        return {
                            answer,
                            answerId: String(answer.id),
                            childQuestId: knownChildQuestId,
                        }
                    }
                } catch (error) {
                    console.error('Error fetching ordinal answer:', error)
                }
            }

            const res = await api.ensureOrdinalThread(item.id)
            setEnsured({ answerId: res.answer_id, childQuestId: res.child_quest_id })
            return { answer: res.answer, answerId: res.answer_id, childQuestId: res.child_quest_id }
        },
        [knownAnswerId, knownChildQuestId, item.id],
    )

    const resolveControlAnswer = useCallback(async (): Promise<Answer | null> => {
        if (resolutionInFlightRef.current) return null
        resolutionInFlightRef.current = true
        try {
            return (await resolveAnswer()).answer || null
        } catch (error) {
            console.error('Error resolving ordinal answer:', error)
            return null
        } finally {
            resolutionInFlightRef.current = false
        }
    }, [resolveAnswer])

    const resolveMenuAnswer = useCallback(async (): Promise<Answer | null> => {
        // Existing wrappers can be fetched anonymously. Materializing a missing
        // wrapper writes, so match Reply and the fullscreen Share control by
        // opening auth instead of sending an anonymous ensure request.
        if (!knownAnswerId && !gon?.currentUser) {
            openAuthModal('signup', 'ordinal_thread')
            return null
        }
        const answer = await resolveControlAnswer()
        return answer ? withOrdinalShareOwner(answer, item.parent?.user?.name) : null
    }, [item.parent?.user?.name, knownAnswerId, openAuthModal, resolveControlAnswer])

    // The system thread's wrapper answer and child quest. Always goes through
    // ensure_thread so a missing child Quest is repaired before use.
    const resolveThread = useCallback(async () => {
        if (resolutionInFlightRef.current) return null
        resolutionInFlightRef.current = true
        try {
            const resolved = await resolveAnswer({ requireThread: true })
            if (!resolved.childQuestId || !resolved.answerId) return null
            return {
                questId: resolved.childQuestId,
                answerId: resolved.answerId,
                answer: resolved.answer || null,
            }
        } catch (error) {
            console.error('Error opening ordinal thread:', error)
            return null
        } finally {
            resolutionInFlightRef.current = false
        }
    }, [resolveAnswer])

    // The caption bar's surface: a thread that already exists opens for
    // anyone; materializing one writes, which guests cannot do.
    const handleOpenThread = useCallback(
        async (ev: React.MouseEvent<HTMLDivElement>) => {
            ev.stopPropagation()
            // Nested controls own their click, and so do portaled menus,
            // whose React events bubble here without being inside the bar.
            if (!ev.currentTarget.contains(ev.target as Node) || surfaceClickIsClaimed(ev)) return
            let target =
                knownAnswerId && knownChildQuestId
                    ? { questId: String(knownChildQuestId), answerId: String(knownAnswerId) }
                    : null
            if (!target) {
                if (!gon?.currentUser) {
                    openAuthModal('signup', 'ordinal_thread')
                    return
                }
                target = await resolveThread()
            }
            if (!target) return
            window.logEv?.('CLICK_ORDINAL_CAPTION_THREAD', { bsv_ordinal_id: item.id })
            onBeforeNavigate?.()
            openRight({ questId: target.questId, answerId: target.answerId })
        },
        [
            item.id,
            knownAnswerId,
            knownChildQuestId,
            onBeforeNavigate,
            openAuthModal,
            openRight,
            resolveThread,
        ],
    )

    // Reply drafts into the system thread in the reply modal, the way a reply
    // from a thread-channel card does.
    const openReplyComposer = useCallback(
        async (questId: string, wrapperAnswer: Answer | null) => {
            let quest: Quest | null = getCachedQuest(questId)
            if (!quest) {
                try {
                    quest = (await api.getQuest(questId)) as Quest | null
                } catch (error) {
                    console.error('Error loading ordinal thread:', error)
                }
            }
            if (!quest?.id) return
            if (!quest.sorted_answers) quest = { ...quest, sorted_answers: [] }

            const draftId = draftAnswerStore.getDraftId(quest.id)
            let draftAnswer: AnswerOrDraft | null = draftId ? getCachedAnswer(draftId) : null
            if (!draftAnswer) {
                draftAnswer = answerModel.newAnswer({
                    quest_id: quest.id,
                    space_id: quest.space_id,
                    is_draft: true,
                })
                cacheAnswer(draftAnswer)
                draftAnswerStore.setDraft(quest.id, draftAnswer.id)
            }

            const targetQuest = quest
            useStore.setState({
                isModalOpen: true,
                modalParams: {
                    type: 'NewAnswer',
                    noHeader: true,
                    quest: targetQuest,
                    newAnswer: draftAnswer,
                    parentAnswer: wrapperAnswer || targetQuest.parent,
                    showReplyTo: true,
                    user: currentUserSnapshot(),
                    showUser: true,
                    showUsername: false,
                    onAnswerPosted: (posted: Answer) => {
                        useStore.setState({ isModalOpen: false })
                        if (!posted?.id) return
                        // Posting is not a navigation intent: the panel offers
                        // "View reply", which opens the system thread right.
                        offerAnswerJump?.(String(posted.id), String(targetQuest.id))
                    },
                    customStyles: {
                        width: '50dvw',
                        minWidth: '600px',
                        maxWidth: '1000px',
                        maxHeight: '80dvh',
                        left: '50%',
                        transform: 'translate(-50%, 0%)',
                    },
                    mobileCustomStyles: {
                        width: '95dvw',
                        maxWidth: '95dvw',
                        minWidth: 'unset',
                        left: '50%',
                        transform: 'translate(-50%, 0%)',
                        top: '2%',
                    },
                },
            })
        },
        [offerAnswerJump],
    )

    const handleReply = useCallback(
        async (ev?: React.MouseEvent) => {
            ev?.stopPropagation?.()
            ev?.preventDefault?.()
            if (!gon?.currentUser) {
                openAuthModal('signup', 'ordinal_thread')
                return
            }
            const thread = await resolveThread()
            if (!thread) return
            const answer = thread.answer || cachedAnswer || null
            if (ordinalChatEnabled()) {
                if (!answer) return
                setChatThread({ answer, questId: thread.questId })
                return
            }
            onBeforeNavigate?.()
            await openReplyComposer(thread.questId, answer)
        },
        [cachedAnswer, onBeforeNavigate, openAuthModal, openReplyComposer, resolveThread],
    )

    // Warm the cache for tiles that already have an answer so the full bar can
    // swap in before the first press. Never calls ensure_thread — that writes.
    const handlePrefetch = useCallback(() => {
        if (prefetchedRef.current || cachedAnswer?.id || !knownAnswerId) return
        prefetchedRef.current = true
        fetchAndCacheOrdinalAnswer<Answer>(knownAnswerId, api.getAnswer, cacheAnswer).catch(() => {
            prefetchedRef.current = false
        })
    }, [cachedAnswer?.id, knownAnswerId])

    const handleViewMintingPost = useCallback(async () => {
        if (!item.origin) return
        const provenance = await loadOrdinalProvenance(item.origin)
        const questId = provenance?.minting_quest_id
        if (!questId) return

        onBeforeNavigate?.()
        openRight({
            questId,
            answerId: provenance.minting_answer_id || item.minting_answer_id || undefined,
        })
    }, [item.minting_answer_id, item.origin, onBeforeNavigate, openRight])

    const isCaption = variant === 'caption'
    const playlistMediaKey = ordinalPlaylistMediaKey({
        contentType: item.content_type,
        bsvOrdinalId: item.id,
        outpoint: item.origin,
        src: item.content_url,
    })
    const dockClassName = isCaption
        ? styles.captionBar
        : cn(styles.actionDock, styles.actionDockFullscreen)

    const controls = (
        <AnswerButtons
            answer={cachedAnswer?.id ? cachedAnswer : previewAnswer}
            theme={isCaption ? undefined : 'play'}
            thin
            onReplyClick={handleReply}
            artifactOrdinalId={item.id}
            artifactOwnerId={item.owner_id || undefined}
            upvalueSource="ordinal_stream"
            resolveAnswer={cachedAnswer?.id ? undefined : resolveControlAnswer}
            displayValueSats={cachedAnswer?.id ? undefined : summaryValueSats}
            valueTestId="ordinal-upvalue-total"
            className={styles.actionDockControls}
            onViewMintingPost={
                item.origin && item.minting_answer_id ? handleViewMintingPost : undefined
            }
        />
    )

    if (!bsvOrdinalControlsEnabled()) return null

    const artifactMenu = item.origin ? (
        <ArtifactMenu
            answer={
                cachedAnswer?.id
                    ? withOrdinalShareOwner(cachedAnswer, item.parent?.user?.name)
                    : knownAnswerId
                    ? withOrdinalShareOwner(previewAnswer, item.parent?.user?.name)
                    : {}
            }
            origin={item.origin}
            onDetails={() => onDetails?.(item)}
            onBeforeNavigate={onBeforeNavigate}
            resolveAnswer={resolveMenuAnswer}
            playlistMediaKey={playlistMediaKey}
            streamOverlay={isCaption}
            fullscreenOverlay={!isCaption}
        />
    ) : null

    const dock = (
        <div
            className={dockClassName}
            data-testid="ordinal-action-dock"
            data-ordinal-id={item.id}
            data-no-modal-interaction
            onClick={isCaption ? handleOpenThread : stopInteractiveEvent}
            onMouseDown={stopInteractiveEvent}
            onMouseUp={stopInteractiveEvent}
            onPointerDown={stopInteractiveEvent}
            onTouchStart={stopInteractiveEvent}
            onTouchEnd={stopInteractiveEvent}
            onMouseEnter={handlePrefetch}
        >
            {isCaption && artifactMenu}
            {isCaption ? controls : <div className={styles.actionDockInner}>{controls}</div>}
        </div>
    )

    return (
        <>
            {isCaption ? (
                dock
            ) : (
                <>
                    {artifactMenu}
                    {dock}
                </>
            )}
            {chatThread && (
                <OrdinalChatModal
                    item={item}
                    wrapperAnswer={chatThread.answer}
                    questId={chatThread.questId}
                    onClose={() => setChatThread(null)}
                    onOpenBranch={({ questId: branchQuestId, answerId }) => {
                        setChatThread(null)
                        onBeforeNavigate?.()
                        openRight({ questId: branchQuestId, answerId })
                    }}
                />
            )}
        </>
    )
}
